import { alpha, AreaViewIcon, makeStyles, SurfaceViewIcon, Typography, useTranslation } from '@paintscout/ui'
import type { MuiTheme as Theme } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { QuoteItemView, RenderableItem, WorkOrderCardSection, WorkOrderLayoutView } from '@paintscout/util/builder'
import {
  applyWorkOrderCardOrder,
  getItems,
  getQuoteOptions,
  getWorkOrderCardLayouts,
  getWorkOrderLayoutKey,
  setLineItemHiddenOnWorkOrder,
  setWorkOrderCardLayout,
  setWorkOrderCardOrder
} from '@paintscout/util/builder'
import DragHandleIcon from '@material-ui/icons/DragHandle'
import classnames from 'classnames'
import type { WorkOrderCardLayout } from 'paintscout'
import React, { useMemo } from 'react'
import type { SortableContainerProps, SortableElementProps } from 'react-sortable-hoc'
import { arrayMove, SortableContainer, SortableElement, SortableHandle } from 'react-sortable-hoc'
import { useQuote } from '../context'
import WorkOrderCardNext from './WorkOrderCardNext'
import { getItemHourTotals } from './WorkOrderItemRows'
import { formatHourTotal } from './workOrderHours'

// Drag handle (react-sortable-hoc) — the icon is styled by the card's `dragHandleWrap`.
const DragHandle = SortableHandle(() => <DragHandleIcon />)

interface SortableCardProps {
  item: RenderableItem
  isFull?: boolean
  showHours?: boolean
  showTotalHours?: boolean
  gridItemClass: string
  gridItemFullClass: string
  onCardClick?: (ev: React.MouseEvent<HTMLElement>) => void
  onToggleHide?: () => void
  onToggleWidth?: () => void
}

const SortableCard: React.ComponentClass<SortableCardProps & SortableElementProps> = SortableElement(
  function SortableCardInner(props: SortableCardProps) {
    const {
      item,
      isFull,
      gridItemClass,
      gridItemFullClass,
      showHours,
      showTotalHours,
      onCardClick,
      onToggleHide,
      onToggleWidth
    } = props
    return (
      <div className={classnames(gridItemClass, { [gridItemFullClass]: isFull })}>
        <WorkOrderCardNext
          item={item}
          showHours={showHours}
          showTotalHours={showTotalHours}
          dragHandle={<DragHandle />}
          isFullWidth={isFull}
          onToggleHide={onToggleHide}
          onToggleWidth={onToggleWidth}
          onClick={onCardClick}
        />
      </div>
    )
  }
)

interface SortableGridProps {
  className: string
  children: React.ReactNode
}

const SortableGrid: React.ComponentClass<SortableGridProps & SortableContainerProps> = SortableContainer(
  function SortableGridInner({ className, children }: SortableGridProps) {
    return <div className={className}>{children}</div>
  }
)

export interface WorkOrderSectionNextProps {
  /** Section heading (e.g. "Areas" / "Surfaces", or "Additional Work" / "Options Added"). */
  title: string
  /** Which item view drives the grid — area cards vs surface (substrate) cards. */
  tableView: QuoteItemView
  /**
   * Which cards the grid holds. Additional Work and Options Added render as their own titled group,
   * marked "Accepted by customer", so a crew can tell them from the bid (ENG-2078).
   */
  section?: WorkOrderCardSection
  showHours?: boolean
  showTotalHours?: boolean
  /** Show the bid's section title (icon + heading). The added-work groups always show theirs. */
  showTitle?: boolean
  onItemClick?: (ev: React.MouseEvent<HTMLElement>, key: string) => void
}

const useStyles = makeStyles(
  (theme: Theme) => {
    const neutral = theme.paintscout.palette.brand.Neutral
    return {
      root: {},
      header: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(2),
        marginBottom: theme.spacing(1.5)
      },
      titleLeft: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        minWidth: 0
      },
      titleIcon: {
        fontSize: 18,
        color: neutral.P60
      },
      // No prototype counterpart (its pills are the only section label), so this follows the card
      // headers: 16px medium in the same title color.
      title: {
        fontWeight: 500,
        fontSize: theme.typography.pxToRem(16),
        color: neutral.P35
      },
      // Additional Work / Options Added: the group's title, its badge and its hours on one ruled line,
      // so the group reads as separate from the bid cards above it (ENG-2078, option B).
      groupHeader: {
        display: 'flex',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: theme.spacing(1, 1.25),
        paddingBottom: theme.spacing(1),
        marginBottom: theme.spacing(1.5),
        borderBottom: `1px solid ${neutral.P90}`
      },
      groupTitle: {
        fontWeight: 600,
        fontSize: theme.typography.pxToRem(16),
        color: neutral.P35
      },
      // A tinted pill in the "accepted" status colour, the same wash the document's status pill uses.
      acceptedBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        padding: theme.spacing(0.125, 1),
        borderRadius: 999,
        fontSize: theme.typography.pxToRem(12),
        fontWeight: 600,
        whiteSpace: 'nowrap',
        color: theme.palette.quote.status.accepted,
        backgroundColor: alpha(theme.palette.quote.status.accepted, 0.12)
      },
      groupHours: {
        marginLeft: 'auto',
        fontSize: theme.typography.pxToRem(13),
        color: neutral.P50
      },
      grid: {
        display: 'grid',
        gridTemplateColumns: '1fr 1fr',
        // Dense auto-flow packs the (mixed half/full-width) cards to the least vertical height.
        gridAutoFlow: 'row dense',
        gap: theme.spacing(2.5),
        alignItems: 'start',
        [theme.breakpoints.down('sm')]: {
          gridTemplateColumns: '1fr'
        }
      },
      gridItem: {
        minWidth: 0
      },
      gridItemFull: {
        gridColumn: '1 / -1'
      },
      // The floating clone while dragging (react-sortable-hoc renders it detached from the grid).
      dragHelper: {
        zIndex: 2_147_483_002,
        cursor: 'grabbing',
        // Matches the card's own outer radius so the floating clone looks like the card it came from.
        borderRadius: theme.borderRadius.xl,
        boxShadow: theme.shadows[6],
        listStyle: 'none'
      }
    }
  },
  { name: 'WorkOrderSectionNext' }
)

/**
 * A Work Order section (Areas / Surfaces) in the redesigned estimateNext style: an icon + title with
 * "Clean up" + "Show all hours" controls, a dense-packed grid of draggable {@link WorkOrderCardNext}
 * cards, and a collapsible "Hidden {title} (N)" panel with Restore. Each card's hover controls hide
 * it (into the panel) or toggle its width (full-width spans both columns); dragging reorders the
 * cards. Hidden/width/order state persists on `quote.workOrderLayout`, scoped to this view and to the
 * work order — none of it touches the estimate's item order.
 */
export default function WorkOrderSectionNext({
  title,
  tableView,
  section = 'bid',
  showHours,
  showTotalHours,
  showTitle = true,
  onItemClick
}: WorkOrderSectionNextProps) {
  const classes = useStyles()
  const { quote, isEditable, updateQuote } = useQuote()
  const { options } = useClientOptions()
  const { __, lang } = useTranslation()
  const quoteOptions = getQuoteOptions({ options, quote })

  // Per-view layout persisted on the quote: per-card overrides (`cards`) + the work order's own card
  // order (`order`), both scoped to the work order and never applied to the estimate.
  const viewKey: WorkOrderLayoutView = tableView === 'substrate' ? 'substrate' : 'area'
  // The same area or substrate can have a card in more than one section, so the layout is keyed by
  // section as well as item. Item actions (click to edit, line-item hide) still use the item key.
  const layoutKey = (key: string) => getWorkOrderLayoutKey(section, key)
  const isAddedWork = section !== 'bid'

  const items = useMemo(() => {
    const items = getItems({
      quote,
      section,
      view: tableView,
      consumer: 'crew',
      options,
      overrideOptions: { showQuantities: true },
      showLineItems: true,
      showValues: showHours,
      hideDimensions: quoteOptions.hideWorkOrderDimensions,
      lang
    })
    // The work order's own card order (`quote.workOrderLayout[view].order`) is applied here rather
    // than written back to `quote.order`, so rearranging cards leaves the estimate alone (ENG-1433).
    return applyWorkOrderCardOrder(items, quote.workOrderLayout?.[viewKey]?.order, (item) =>
      getWorkOrderLayoutKey(section, item.key)
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quote, tableView, section, showHours])

  const layout = getWorkOrderCardLayouts(quote, viewKey)
  // The account's "Display Work Order Items in Single Column" setting makes full width the default;
  // a card's own `width` still overrides it either way.
  const defaultWidth = 'full' // options.options.quotes?.singleColumnWorkOrder === true ? 'full' : 'half'
  const isFullWidth = (key: string) => (layout[layoutKey(key)]?.width ?? defaultWidth) === 'full'
  const isHidden = (key: string) => !!layout[layoutKey(key)]?.hidden
  const visibleItems = items.filter((item) => !isHidden(item.key))

  if (!items.length) {
    return null
  }

  const patchLayout = (key: string, patch: WorkOrderCardLayout) =>
    updateQuote({ quote: setWorkOrderCardLayout({ quote, view: viewKey, key: layoutKey(key), patch }) })

  // Line items carry their own hide flag — the estimate's "Hidden on Work Order" checkbox — so their
  // cards write that instead of the per-view layout. One flag, both screens: ticking the checkbox on
  // the estimate drops the card here, and hiding the card here ticks the checkbox (ENG-1930).
  const handleToggleHide = (key: string) => {
    const item = items.find((i) => i.key === key)
    if (item?.type === 'line_item') {
      updateQuote({ quote: setLineItemHiddenOnWorkOrder({ quote, key, hidden: true }) })
      return
    }
    patchLayout(key, { hidden: !isHidden(key) })
  }
  // Store only a departure from the default, so toggling back to the default clears the override.
  const handleToggleWidth = (key: string) => {
    const nextWidth = isFullWidth(key) ? 'half' : 'full'
    patchLayout(key, { width: nextWidth === defaultWidth ? undefined : nextWidth })
  }

  // Reorder the visible cards, rebuild the full ordered list keeping hidden cards in place, and save
  // it as this view's work-order card order. Only `quote.workOrderLayout` changes — the estimate's
  // item order stays where the estimator put it (ENG-1433).
  const handleSortEnd = ({ oldIndex, newIndex }: { oldIndex: number; newIndex: number }) => {
    if (oldIndex === newIndex) {
      return
    }
    const reorderedVisible = arrayMove(visibleItems, oldIndex, newIndex)
    let vi = 0
    const fullOrder = items.map((item) => (isHidden(item.key) ? item : reorderedVisible[vi++]))
    updateQuote({
      quote: setWorkOrderCardOrder({ quote, view: viewKey, keys: fullOrder.map((item) => layoutKey(item.key)) })
    })
  }

  // The group's hours, from the cards on screen — a hidden card's hours aren't the crew's to see.
  const groupHours =
    isAddedWork && showHours && showTotalHours
      ? visibleItems.reduce((total, item) => total + getItemHourTotals(item).total, 0)
      : 0

  return (
    <div className={classes.root}>
      {isAddedWork && (
        <div className={classes.groupHeader} data-testid={`work-order-group-${section}`}>
          <Typography component="h3" className={classes.groupTitle}>
            {title}
          </Typography>
          <span className={classes.acceptedBadge}>{__('Accepted by customer')}</span>
          {groupHours > 0 && (
            <span className={classes.groupHours}>{`${formatHourTotal(groupHours, lang)} ${__('hrs')}`}</span>
          )}
        </div>
      )}

      {/* Title (icon + heading). Clean up for this view lives in the top nav bar, not here. */}
      {showTitle && !isAddedWork && (
        <div className={classes.header}>
          <div className={classes.titleLeft}>
            {tableView === 'substrate' ? (
              <SurfaceViewIcon className={classes.titleIcon} />
            ) : (
              <AreaViewIcon className={classes.titleIcon} />
            )}
            <Typography className={classes.title}>{title}</Typography>
          </div>
        </div>
      )}

      <SortableGrid
        className={classes.grid}
        axis="xy"
        useDragHandle
        useWindowAsScrollContainer
        helperClass={classes.dragHelper}
        onSortEnd={handleSortEnd}
      >
        {visibleItems.map((item: RenderableItem, index: number) => (
          <SortableCard
            key={item.key}
            index={index}
            item={item}
            isFull={isFullWidth(item.key)}
            showHours={showHours}
            showTotalHours={showTotalHours}
            gridItemClass={classes.gridItem}
            gridItemFullClass={classes.gridItemFull}
            onCardClick={onItemClick ? (ev) => onItemClick(ev, item.key) : undefined}
            onToggleHide={isEditable ? () => handleToggleHide(item.key) : undefined}
            onToggleWidth={isEditable ? () => handleToggleWidth(item.key) : undefined}
          />
        ))}
      </SortableGrid>
    </div>
  )
}
